function stable(value) {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stable(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

function same(left, right) {
  return stable(left) === stable(right);
}

function indexById(list) {
  const map = new Map();
  (list || []).forEach((item) => {
    if (item && typeof item.id === 'string') map.set(item.id, item);
  });
  return map;
}

function mergeCollection(base, server, local, combine) {
  const baseMap = indexById(base);
  const serverMap = indexById(server);
  const localMap = indexById(local);
  const merged = [];
  const seen = new Set();

  (local || []).forEach((item) => {
    if (!item || typeof item.id !== 'string' || seen.has(item.id)) return;
    seen.add(item.id);
    const previous = baseMap.get(item.id);
    const remote = serverMap.get(item.id);
    if (previous && !remote && same(previous, item)) return;
    if (!remote) {
      merged.push(item);
      return;
    }
    merged.push(combine(previous, remote, item));
  });

  (server || []).forEach((item) => {
    if (!item || typeof item.id !== 'string' || seen.has(item.id)) return;
    if (baseMap.has(item.id) && !localMap.has(item.id)) return;
    merged.push(item);
  });

  return merged;
}

function mergeTasks(base, server, local) {
  return mergeCollection(base, server, local, (previous, remote, item) =>
    previous && same(item, previous) ? remote : item
  );
}

function mergeLists(base, server, local) {
  return mergeCollection(base, server, local, (previous, remote, item) => ({
    id: item.id,
    title: previous && same(item.title, previous.title) ? remote.title : item.title,
    tasks: mergeTasks(previous?.tasks, remote.tasks, item.tasks),
  }));
}

function mergePages(base, server, local) {
  return mergeCollection(base, server, local, (previous, remote, item) => ({
    id: item.id,
    name: previous && same(item.name, previous.name) ? remote.name : item.name,
    lists: mergeLists(previous?.lists, remote.lists, item.lists),
  }));
}

function mergeDays(base = {}, server = {}, local = {}) {
  const keys = new Set([...Object.keys(base), ...Object.keys(server), ...Object.keys(local)]);
  const days = {};
  keys.forEach((key) => {
    const tasks = mergeTasks(base[key], server[key], local[key]);
    if (tasks.length) days[key] = tasks;
  });
  return days;
}

function pick(base, server, local) {
  return same(local, base) ? (server === undefined ? local : server) : local;
}

function mergePreferences(base = {}, server = {}, local = {}) {
  const keys = new Set([...Object.keys(base), ...Object.keys(server), ...Object.keys(local)]);
  const preferences = {};
  keys.forEach((key) => {
    preferences[key] = pick(base[key], server[key], local[key]);
  });
  return preferences;
}

function mergeProfile(base = {}, server = {}, local = {}) {
  return {
    name: pick(base.name, server.name, local.name),
    waitlist: pick(base.waitlist, server.waitlist, local.waitlist),
  };
}

export function mergeBoards(base, server, local) {
  const previous = base && typeof base === 'object' ? base : {};
  const remote = server && typeof server === 'object' ? server : {};
  const mine = local && typeof local === 'object' ? local : {};
  return {
    preferences: mergePreferences(previous.preferences, remote.preferences, mine.preferences),
    dayOffset: pick(previous.dayOffset, remote.dayOffset, mine.dayOffset),
    days: mergeDays(previous.days, remote.days, mine.days),
    recurring: mergeTasks(previous.recurring, remote.recurring, mine.recurring),
    pages: mergePages(previous.pages, remote.pages, mine.pages),
    activePageId: pick(previous.activePageId, remote.activePageId, mine.activePageId),
    listsExpanded: pick(previous.listsExpanded, remote.listsExpanded, mine.listsExpanded),
    listsHeight: pick(previous.listsHeight, remote.listsHeight, mine.listsHeight),
    profile: mergeProfile(previous.profile, remote.profile, mine.profile),
  };
}
